import type { ReaderPreferences } from './types'
import { OFFLINE_BUDGET_OPTIONS_MB } from './types'

export const STORAGE_KEY = 'qlearn:prefs:v1'
const SCHEMA_VERSION = 1

type StoredShape = {
  v: number
  prefs: ReaderPreferences
}

export function read(): ReaderPreferences | null {
  let raw: string | null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) return null
  const obj = parsed as Partial<StoredShape>
  if (obj.v !== SCHEMA_VERSION) return null
  if (!obj.prefs || typeof obj.prefs !== 'object') return null
  // Sanitize: 'mushaf' view mode was removed; coerce to 'verses'.
  const p = obj.prefs as ReaderPreferences & {
    viewMode?: string
    soundEffectsEnabled?: boolean
    bismillahBeforeSurah?: boolean
  }
  if (p.viewMode !== 'verses' && p.viewMode !== 'book') {
    p.viewMode = 'verses'
  }
  // soundEffectsEnabled was added after the v1 schema shipped. Default to true
  // for legacy stored prefs so existing users still get the new sound effects
  // without an opt-in — they can disable from Settings.
  if (typeof p.soundEffectsEnabled !== 'boolean') {
    p.soundEffectsEnabled = true
  }
  // bismillahBeforeSurah was added after the v1 schema shipped. Default true
  // so existing users get the Bismillah preamble by default; the Settings
  // page has the toggle for users whose reciter already bakes it in.
  // Without this, the field arrives undefined on hydration and the Switch
  // switches uncontrolled→controlled on next render, triggering a React
  // warning ("changing an uncontrolled input to be controlled").
  if (typeof p.bismillahBeforeSurah !== 'boolean') {
    p.bismillahBeforeSurah = true
  }
  // Offline-suite prefs added after v1 shipped: default and validate.
  const pAny = p as ReaderPreferences & { offlineDataBudgetMB?: unknown; autoDownloadOnPlay?: unknown }
  if (
    typeof pAny.offlineDataBudgetMB !== 'number' ||
    !(OFFLINE_BUDGET_OPTIONS_MB as readonly number[]).includes(pAny.offlineDataBudgetMB)
  ) {
    pAny.offlineDataBudgetMB = 500
  }
  if (typeof pAny.autoDownloadOnPlay !== 'boolean') {
    pAny.autoDownloadOnPlay = false
  }
  // kidMode is intentionally NOT persisted locally — see write() below.
  // The parental gate's purpose is to prevent a child from disabling kid
  // mode without the PIN; persisting the flag locally would defeat this
  // (devtools → edit localStorage → reload, transient until the next
  // profile sync). The server is the authoritative store. On rehydration
  // we always start at false and let PreferencesContext's profile-fetch
  // effect set the truth.
  ;(obj.prefs as ReaderPreferences).kidMode = false
  return obj.prefs
}

export function write(prefs: ReaderPreferences): void {
  // Strip kidMode before persisting. The parental gate's purpose is to
  // prevent a child from flipping kid mode off without the PIN; persisting
  // the flag locally lets a curious kid edit localStorage in devtools to
  // bypass the gate (transient until the next profile sync, but still bad
  // UX). The server is the only authoritative store for kidMode.
  const stripped: ReaderPreferences = { ...prefs, kidMode: false }
  const blob: StoredShape = { v: SCHEMA_VERSION, prefs: stripped }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(blob))
  } catch {
    // Quota exceeded, private mode, etc. — silent.
  }
}
